import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Zap, TrendingDown, MessageSquare, FileText as FileIcon, Megaphone, Clock, Sparkles, ChevronDown, ChevronUp, Database } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("narwhal_auth_token") || localStorage.getItem("token") || ""}`,
});

async function fetchInsights(week) {
  const url = week ? `${API}/api/admin/insights?week=${week}` : `${API}/api/admin/insights`;
  const r = await axios.get(url, { headers: authHeader() });
  return r.data;
}
async function fetchWeeks() {
  const r = await axios.get(`${API}/api/admin/insights/weeks`, { headers: authHeader() });
  return r.data?.weeks || [];
}
async function fetchClientSignals(clientId, week) {
  const url = week
    ? `${API}/api/admin/insights/${clientId}/signals?week=${week}`
    : `${API}/api/admin/insights/${clientId}/signals`;
  const r = await axios.get(url, { headers: authHeader() });
  return r.data;
}

async function refreshInsights({ week, force, clientId } = {}) {
  const params = new URLSearchParams();
  if (week) params.set("week", week);
  if (force) params.set("force", "true");
  if (clientId) params.set("client_id", clientId);
  // Per-client refresh is short and safe to run inline; full refresh goes to background
  if (!clientId) params.set("background", "true");
  else params.set("background", "false");
  const r = await axios.post(`${API}/api/admin/insights/refresh?${params}`, {}, { headers: authHeader() });
  return r.data;
}
async function fetchRefreshStatus() {
  const r = await axios.get(`${API}/api/admin/insights/refresh-status`, { headers: authHeader() });
  return r.data || { status: "idle" };
}

const SEVERITY_STYLES = {
  high: { chip: "bg-rose-100 text-rose-800 border-rose-200", bar: "bg-rose-500", icon: AlertTriangle, label: "Alta" },
  medium: { chip: "bg-amber-100 text-amber-800 border-amber-200", bar: "bg-amber-500", icon: TrendingDown, label: "Media" },
  low: { chip: "bg-slate-100 text-slate-700 border-slate-200", bar: "bg-slate-400", icon: Clock, label: "Baja" },
};
const SOURCE_ICONS = {
  skylead: MessageSquare,
  meetings: Zap,
  publications: FileIcon,
  ads: Megaphone,
  tasks: Clock,
  project: AlertTriangle,
};

function fmtDateShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short" }); }
  catch { return iso; }
}

export default function InsightsPage() {
  const qc = useQueryClient();
  const [week, setWeek] = useState("");

  const weeksQ = useQuery({ queryKey: ["insights-weeks"], queryFn: fetchWeeks });
  const q = useQuery({
    queryKey: ["insights", week],
    queryFn: () => fetchInsights(week || null),
  });

  // Job status polling (only active when a job is running)
  const [pollActive, setPollActive] = useState(false);
  const statusQ = useQuery({
    queryKey: ["insights-refresh-status"],
    queryFn: fetchRefreshStatus,
    enabled: pollActive,
    refetchInterval: pollActive ? 3000 : false,
  });

  // On mount, check if a background refresh is already running
  useEffect(() => {
    fetchRefreshStatus().then((s) => {
      if (s?.status === "running") setPollActive(true);
    }).catch(() => {});
  }, []);
  const status = statusQ.data;
  // Auto-invalidate list when job completes
  useEffect(() => {
    if (!pollActive) return;
    if (status?.status === "done") {
      setPollActive(false);
      qc.invalidateQueries({ queryKey: ["insights"] });
      qc.invalidateQueries({ queryKey: ["insights-weeks"] });
      toast.success(`Refresh completo · ${status.result?.processed || 0} clientes`);
    } else if (status?.status === "error") {
      setPollActive(false);
      toast.error(`Error: ${status.error || "desconocido"}`);
    }
  }, [pollActive, status, qc]);

  const refresh = useMutation({
    mutationFn: (opts) => refreshInsights(opts),
    onSuccess: (res, vars) => {
      if (vars?.clientId) {
        qc.invalidateQueries({ queryKey: ["insights"] });
        toast.success("Cliente re-analizado");
      } else {
        // background job → start polling
        setPollActive(true);
        toast.info("Corriendo · esto tarda 2-5 min. Vas a ver el progreso arriba.");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo iniciar"),
  });

  const clients = q.data?.clients || [];
  const summary = q.data?.summary || { total: 0, all_ok: 0, high_alerts: 0 };
  const activeWeek = week || q.data?.week;
  const period = clients[0]?.period;

  return (
    <div className="max-w-7xl mx-auto py-8 px-6" data-testid="insights-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <h1 className="font-display text-2xl font-semibold text-slate-900">Insights semanales</h1>
            <span className="text-[10px] px-2 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded font-semibold uppercase tracking-wide">Claude Sonnet 5</span>
          </div>
          <p className="text-sm text-slate-500 max-w-3xl">
            Análisis IA por cliente sobre la <b>semana calendario cerrada</b> (Lun→Dom ARG). Solo aparecen alertas cuando hay algo que hace ruido — outreach parado, plata de ads sin conversión, contenido no publicado, tareas dormidas, etc.
          </p>
          {period && (
            <div className="text-xs text-slate-500 mt-1">
              Período: {fmtDateShort(period.monday)} → {fmtDateShort(period.sunday)} · {activeWeek}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={week}
            onChange={(e) => setWeek(e.target.value)}
            className="text-xs px-3 py-2 border border-slate-200 rounded bg-white"
            data-testid="insights-week-select"
          >
            <option value="">Semana más reciente</option>
            {(weeksQ.data || []).map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <Button
            onClick={() => refresh.mutate({ week: week || undefined, force: true })}
            disabled={refresh.isPending}
            className="gap-1.5 text-xs h-9 bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="insights-refresh-btn"
          >
            {refresh.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Regenerar todos
          </Button>
        </div>
      </div>

      {/* Job progress banner */}
      {pollActive && (
        <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-md px-4 py-3 flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-600 shrink-0" />
          <div className="flex-1 text-sm text-indigo-900">
            <b>Regenerando reportes + análisis IA de todos los clientes…</b>
            <div className="text-xs text-indigo-700 mt-0.5">
              Cada cliente arranca con snapshot nuevo. Puede tardar 2-5 min. Podés cerrar la pestaña — sigue corriendo.
            </div>
          </div>
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-md px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Clientes analizados</div>
          <div className="text-2xl font-display font-semibold text-slate-900 mt-0.5">{summary.total}</div>
        </div>
        <div className="bg-white border border-emerald-200 rounded-md px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Todo OK</div>
          <div className="text-2xl font-display font-semibold text-emerald-700 mt-0.5">{summary.all_ok}</div>
        </div>
        <div className="bg-white border border-rose-200 rounded-md px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Alertas HIGH</div>
          <div className="text-2xl font-display font-semibold text-rose-700 mt-0.5">{summary.high_alerts}</div>
        </div>
      </div>

      {/* Grid de clientes */}
      {q.isLoading ? (
        <div className="py-16 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Cargando análisis…</div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md px-6 py-16 text-center">
          <Sparkles className="h-6 w-6 text-slate-300 mx-auto mb-2" />
          <h3 className="font-display text-base font-semibold text-slate-900 mb-1">Sin análisis para esta semana</h3>
          <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">Tocá &quot;Regenerar todos&quot; para que Claude analice los reportes semanales de cada cliente. Se ejecuta también automático los lunes a las 09:00 ARG.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {clients.map((c) => (
            <InsightCard
              key={c.client_id}
              data={c}
              activeWeek={activeWeek}
              onRegenerate={() => refresh.mutate({ week: activeWeek, force: true, clientId: c.client_id })}
              regenerating={refresh.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InsightCard({ data, activeWeek, onRegenerate, regenerating }) {
  const [showSignals, setShowSignals] = useState(false);
  const alerts = data.alerts || [];
  const allOk = data.all_ok && alerts.length === 0;
  const hasError = !!data.error;
  const highCount = alerts.filter((a) => a.severity === "high").length;

  const borderTone = allOk ? "border-l-emerald-500"
    : highCount ? "border-l-rose-500"
    : alerts.length ? "border-l-amber-500"
    : "border-l-slate-300";

  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${borderTone} rounded-md overflow-hidden`} data-testid={`insight-card-${data.client_id}`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{data.client_name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Analizado {fmtDateShort(data.generated_at)} · {data.model}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {allOk && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
              <CheckCircle2 className="h-3 w-3" /> Todo OK
            </span>
          )}
          {!allOk && highCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
              <AlertTriangle className="h-3 w-3" /> {highCount} high
            </span>
          )}
          <button
            onClick={onRegenerate}
            disabled={regenerating}
            className="text-slate-400 hover:text-slate-900 p-1 rounded hover:bg-slate-50 disabled:opacity-50"
            title="Re-analizar este cliente (regenera reporte + insights)"
            data-testid={`insight-regen-${data.client_id}`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-2">
        {hasError && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-500">
            <b>Error del analizador:</b> {data.error}
          </div>
        )}
        {!hasError && allOk && (
          <div className="text-sm text-slate-500 italic py-2">
            No hay señales de alerta esta semana. Buen trabajo.
          </div>
        )}
        {!hasError && !allOk && alerts.length === 0 && (
          <div className="text-sm text-slate-400 italic py-2">
            Sin datos suficientes para analizar (¿generaste el reporte semanal?).
          </div>
        )}
        {alerts.map((a, i) => {
          const style = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.low;
          const SourceIcon = SOURCE_ICONS[a.source] || AlertTriangle;
          return (
            <div key={i} className="flex items-start gap-2 py-1.5 text-sm">
              <div className={`h-1.5 w-1.5 rounded-full ${style.bar} mt-2 shrink-0`} />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase tracking-wide ${style.chip}`}>
                    {style.label}
                  </span>
                  {a.source && (
                    <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                      <SourceIcon className="h-3 w-3" />
                      {a.source}
                    </span>
                  )}
                </div>
                <p className="text-slate-700 leading-relaxed text-[13px]">{a.text}</p>
              </div>
            </div>
          );
        })}

        {/* Toggle datos usados */}
        <div className="pt-3 mt-2 border-t border-slate-100">
          <button
            onClick={() => setShowSignals((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid={`insight-signals-toggle-${data.client_id}`}
          >
            <Database className="h-3 w-3" />
            {showSignals ? "Ocultar datos usados" : "Ver datos usados"}
            {showSignals ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {showSignals && (
            <SignalsPanel clientId={data.client_id} week={activeWeek} inline={data.signals} />
          )}
        </div>
      </div>
    </div>
  );
}

function SignalsPanel({ clientId, week, inline }) {
  // Prefer the inline signals blob (already in the list response), only fetch
  // if it's missing.
  const q = useQuery({
    queryKey: ["insight-signals", clientId, week],
    queryFn: () => fetchClientSignals(clientId, week),
    enabled: !inline,
  });
  const signals = inline || q.data?.signals;

  if (!signals && q.isLoading) {
    return <div className="text-xs text-slate-400 mt-2 py-2"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Cargando…</div>;
  }
  if (!signals) return <div className="text-xs text-slate-400 mt-2">Sin datos</div>;
  if (!signals.has_snapshot) {
    return (
      <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2 text-amber-800">
        No hay snapshot semanal para este cliente. Tocá el ícono ↻ para generarlo + re-analizar.
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3 text-xs">
      {signals.skylead_actions && (
        <SignalBlock title="Skylead · Antepenúltima vs Última semana">
          <div className="grid grid-cols-3 gap-x-3 gap-y-1">
            <div className="text-[10px] text-slate-500 uppercase font-semibold">Acción</div>
            <div className="text-[10px] text-slate-500 uppercase font-semibold text-right">Antepenúlt.</div>
            <div className="text-[10px] text-slate-500 uppercase font-semibold text-right">Última</div>
            {Object.entries(signals.skylead_actions).map(([k, v]) => {
              const pct = v.mom_pct;
              const pctColor = pct == null ? "text-slate-500" : pct > 0 ? "text-emerald-600" : pct < -30 ? "text-rose-600" : "text-amber-600";
              return (
                <div key={k} className="contents">
                  <div className="text-slate-700 truncate" title={k}>{k}</div>
                  <div className="text-slate-500 text-right">{v.antepenultima ?? "—"}</div>
                  <div className="text-slate-900 text-right font-medium">
                    {v.ultima ?? "—"}
                    {pct != null && (
                      <span className={`ml-1 text-[10px] ${pctColor}`}>({pct > 0 ? "+" : ""}{pct}%)</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SignalBlock>
      )}

      {signals.skylead_period_totals && (
        <SignalBlock title="Skylead · Totales del período (Lun→Dom)">
          <div className="flex flex-wrap gap-3">
            {Object.entries(signals.skylead_period_totals).map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 rounded px-2 py-1">
                <span className="text-[10px] text-slate-500 uppercase mr-1">{k}</span>
                <span className="text-slate-900 font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </SignalBlock>
      )}

      {signals.meetings && (
        <SignalBlock title="Reuniones">
          <div className="flex gap-4">
            <div><span className="text-slate-500">Realizadas en período:</span> <b className="text-slate-900">{signals.meetings.realizadas_periodo}</b></div>
            <div><span className="text-slate-500">Próximas 14d:</span> <b className="text-slate-900">{signals.meetings.proximas_14d}</b></div>
          </div>
          {(signals.meetings.muestra_realizadas || []).length > 0 && (
            <ul className="mt-1 space-y-0.5 text-slate-600">
              {signals.meetings.muestra_realizadas.slice(0, 3).map((m, i) => (
                <li key={i}>· {m.empresa || "—"} <span className="text-slate-400">· {m.campaign || "—"}</span></li>
              ))}
            </ul>
          )}
        </SignalBlock>
      )}

      {signals.publications && (
        <SignalBlock title="Publicaciones del período">
          <div className="flex items-center gap-3 flex-wrap">
            <div><span className="text-slate-500">Publicadas:</span> <b className="text-slate-900">{signals.publications.total_publicados_periodo}</b></div>
            {signals.publications.total_items_periodo !== signals.publications.total_publicados_periodo && (
              <div><span className="text-slate-500">Total items:</span> {signals.publications.total_items_periodo}</div>
            )}
            {Object.entries(signals.publications.by_type || {}).map(([t, n]) => (
              <span key={t} className="bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-[11px]">
                {t}: <b>{n}</b>
              </span>
            ))}
          </div>
        </SignalBlock>
      )}

      {signals.tasks && (
        <SignalBlock title={`Tareas · ${signals.tasks.total_pending} pendientes · ${signals.tasks.dormant_count} dormidas`}>
          {(signals.tasks.dormant || []).slice(0, 6).map((t, i) => (
            <div key={i} className="text-slate-700 truncate">
              · <b>{t.title}</b> <span className="text-slate-400">· {t.vertical || t.status || t.reason}</span>
            </div>
          ))}
        </SignalBlock>
      )}

      {signals.project_status && (
        <SignalBlock title="Estado del proyecto">
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {signals.project_status.semaforo_label && (
              <div><span className="text-slate-500">Semáforo:</span> <b>{signals.project_status.semaforo_label}</b></div>
            )}
            {signals.project_status.reuniones_alcanzadas != null && (
              <div><span className="text-slate-500">Alcanzadas:</span> <b>{signals.project_status.reuniones_alcanzadas}</b></div>
            )}
            {signals.project_status.objetivo_reuniones ? (
              <div><span className="text-slate-500">Objetivo:</span> <b>{signals.project_status.objetivo_reuniones}</b></div>
            ) : null}
            {signals.project_status.reuniones_proyectadas ? (
              <div><span className="text-slate-500">Proyectadas:</span> <b>{signals.project_status.reuniones_proyectadas}</b></div>
            ) : null}
            {signals.project_status.dias_restantes ? (
              <div><span className="text-slate-500">Días rest.:</span> <b>{signals.project_status.dias_restantes}</b></div>
            ) : null}
          </div>
        </SignalBlock>
      )}

      {signals.ads_bitacora && (
        <SignalBlock title={`Bitácora Ads · ${signals.ads_bitacora.count} entrada${signals.ads_bitacora.count === 1 ? "" : "s"}`}>
          {(signals.ads_bitacora.sample || []).map((b, i) => (
            <div key={i} className="bg-slate-50 border border-slate-100 rounded px-2 py-1.5 mb-1">
              <div className="text-slate-500 text-[10px]">{b.period} · Inversión {b.investment || "—"}</div>
              <div className="text-slate-700 mt-0.5">{b.summary}</div>
            </div>
          ))}
        </SignalBlock>
      )}
    </div>
  );
}

function SignalBlock({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{title}</div>
      <div>{children}</div>
    </div>
  );
}
